import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { LanguageToolPage } from '@/components/astro/new-design-25/SEO/language-tool-page';
import { hreflangLinks, seo } from '@/seo/seo';

const faqTitle = 'Italian text to speech FAQ';
const faqDescription =
  'Quick answers about converting Italian text to speech with Listening.';
const faqs = [
  {
    question: 'Which Italian voices are available?',
    answer:
      'Listening offers natural Italian voices in male and female options, trained on real Italian speech with correct musicality and rhythm rather than flat foreign-language imitation.',
  },
  {
    question: 'How are double consonants and stressed vowels handled?',
    answer:
      'Italian doubles (pp, tt, ll, ss) pronounce as the lengthened sounds they actually are, and stress falls on the correct syllable in Italian and Latinate vocabulary.',
  },
  {
    question: 'Can I use this for Italian academic content?',
    answer:
      'Yes. The voices are trained on Italian academic, literary, and journalistic prose. Art history, philosophy, and classical literature pronounce naturally with proper register.',
  },
  {
    question: 'Is it suitable for Italian learners?',
    answer:
      'Yes. Slow the playback to 0.7x, paste any Italian article or chapter, and shadow native pronunciation. Synced highlighting in the Listening app helps map sound to written form.',
  },
];

const heroTitle = 'Italian Text to Speech';
const heroDescription =
  'Italian text to speech with natural Italian voices. Designed for academic content, literature, language learning, and professional Italian audio, with proper pronunciation of double consonants, accents, and Italian academic vocabulary.';

const featureTitle = 'the Italian TTS Italian speakers actually keep using:';
const featureSubtitle = '';
const featureDescription = '';
const featureBadge = 'Italian voices';
const featureVariant = 'default';
const featureCtaText = 'Try now';

const featureFeatures = [
  {
    title: 'Natural Italian Voices',
    description:
      'Voices trained on real Italian speech with correct intonation and rhythm, not Spanish or Latin TTS pretending to be Italian.',
  },
  {
    title: 'Doubles and Accents Handled',
    description:
      'Italian double consonants (pp, tt, ll, ss) and stressed vowels are pronounced correctly, which most free TTS gets wrong.',
  },
  {
    title: 'Academic and Literary Vocabulary',
    description:
      'Handles Italian academic prose, art history, philosophy, and classical literature with proper register.',
  },
  {
    title: 'Right for Language Learners',
    description:
      'Slow speeds and synced highlighting in the app make this excellent for learners building Italian listening skill.',
  },
];

const solutionBenefitsData = {
  title: 'Why Italian listeners stay with our Italian TTS',
  items: [
    {
      text: 'Natural Italian voices with the right musicality, not a flat foreign-language imitation',
      highlight: 'natural Italian voices',
    },
    {
      text: 'Correct pronunciation of double consonants, stressed syllables, and accented vowels',
      highlight: 'double consonants, stressed syllables',
    },
    {
      text: 'Reliable handling of Italian academic, artistic, and historical terminology',
      highlight: 'academic, artistic, and historical',
    },
    {
      text: 'Adjustable speed from 0.5x to 4x for shadowing, review, and long-form listening',
      highlight: '0.5x to 4x',
    },
  ],
};

const useCasesData = {
  title: 'Real ways people use Italian text to speech',
  description:
    'Students at Italian universities, art historians, language learners, and professionals producing Italian content.',
  useCases: [
    {
      title: 'Student at an Italian University',
      challenge:
        'Get through dense Italian academic readings in fields like law, philosophy, art history, or letteratura.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Convert assigned readings to Italian audio for revision on the train, in the library, or before exams.',
        },
        {
          label: 'Generic TTS',
          content:
            'Broken pronunciation on academic Italian vocabulary makes a 30-page reading sound like noise after 10 minutes.',
        },
      ],
    },
    {
      title: 'Italian Learner Building Listening Skill',
      challenge:
        'Move past textbook dialogues and start consuming real Italian articles, essays, and short stories.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Paste any Italian text, slow the playback to 0.7x, and shadow native pronunciation with synced highlighting.',
        },
        {
          label: 'Generic TTS',
          content:
            'Flat, unmusical phrasing trains the wrong rhythm into your ear and slows down real comprehension later.',
        },
      ],
    },
    {
      title: 'Art Historian or Curator',
      challenge:
        'Review Italian-language exhibition catalogs, academic papers, and primary sources without manually reading every page.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Generate clean Italian audio of long art-historical texts, with proper pronunciation of proper nouns and period vocabulary.',
        },
        {
          label: 'Generic TTS',
          content:
            'Mangles names of artists, schools, and movements, which makes the audio useless for serious research.',
        },
      ],
    },
    {
      title: 'Italian Content Creator',
      challenge:
        'Produce Italian audio versions of articles, newsletters, or course material at consistent quality.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Use the same Italian voice across all output for a recognizable identity, with 24-hour read-along links.',
        },
        {
          label: 'Generic TTS',
          content:
            'Voices shift in quality, accent, or naturalness between sessions, undermining the brand of the content.',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'What Italian Text to Speech Has to Get Right',
    content:
      'Italian sounds simple on paper — every letter pronounced, no silent vowels, regular spelling. In practice, generic Italian TTS still fails because it misses the things that make Italian sound Italian: the length of double consonants, the stress falling on the right syllable, the music of long sentences with embedded clauses. A flat reading of Italian is immediately wrong to any native speaker, no matter how technically correct the phonemes are.',
    contentSecondary:
      'Listening was trained on Italian academic, literary, and journalistic content read by native speakers. Our voices pronounce double consonants as the lengthened sounds they actually are, stress the right syllables in Italian and Latinate vocabulary, and keep the natural cadence of Italian prose. The result is audio you can listen to for a chapter or a paper without an Italian ear flinching every two sentences.',
  },
  {
    title: 'Italian Text to Speech for Study, Research, and Learning',
    content:
      'For Italian university students and researchers, listening is a way to keep up with the sheer volume of reading expected. Italian academic prose tends to be long and clause-heavy, which is exhausting on the page but actually clearer when you hear it. For Italian learners outside Italy, the converter on this page is the easiest way to hear any Italian text you want at exactly the speed you can handle, with synced highlighting available in the Listening app.',
    contentSecondary:
      'The free converter opens a 24-hour read-along link with synced text, audio, speed controls, and a playbar. The same Italian voices are available in the Listening web app, mobile apps, and Chrome extension, so the language you study at home travels with you for the rest of the day. Try the free converter on this page to feel the difference between Italian TTS that sounds right and Italian TTS that does not.',
  },
];

export const Route = createFileRoute('/_marketing/_seo/italian-text-to-speech')(
  {
    beforeLoad: assertPublicToolsEnabled,
    head: () => ({
      meta: [
        ...seo({
          title:
            'Italian Text to Speech — Natural Italian AI Voices | Listening',
          description:
            'Italian text to speech with natural Italian AI voices. Designed for academic content, literature, and language learning. Free demo, no sign-up.',
        }),
      ],
      links: [...hreflangLinks('/italian-text-to-speech')],
    }),
    component: () => (
      <LanguageToolPage
        converterHeading={heroTitle}
        converterSubtitle={heroDescription}
        faqDescription={faqDescription}
        faqTitle={faqTitle}
        faqs={faqs}
        featureBadge={featureBadge}
        featureCtaText={featureCtaText}
        featureDescription={featureDescription}
        featureFeatures={featureFeatures}
        featureSubtitle={featureSubtitle}
        featureTitle={featureTitle}
        featureVariant={featureVariant}
        preferredLang='it'
        seoSections={seoSections}
        solutionBenefitsData={solutionBenefitsData}
        useCasesData={useCasesData}
      />
    ),
  },
);
